import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BrowserService } from './browser.service';

export interface Language {
  code: string;
  name: string;
  flag: string;
  direction: 'ltr' | 'rtl';
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  // Only static/hardcoded text translations - NO database data
  private translations: { [language: string]: { [key: string]: string } } = {
    en: {
      // Navigation
      'nav.home': 'Home',
      'nav.shop': 'Products',
      'nav.about': 'About',
      'nav.contact': 'Contact',
      'nav.login': 'Login',
      'nav.signup': 'Sign Up',
      'nav.profile': 'Profile',
      'nav.orders': 'Orders',
      'nav.favorites': 'Favorites',
      'nav.cart': 'Cart',
      'nav.logout': 'Logout',
      'nav.language': 'Language',
      'nav.search': 'Search',
      'nav.categories': 'Categories',
      'nav.deliverTo': 'Deliver to',
      'nav.policies': 'Policies',
      'nav.customerSupport': 'Customer Support',
      'nav.updatePassword': 'Update Password',
      'nav.orderStatus': 'Order Status',
      'nav.browsingHistory': 'Browsing History',
      'nav.noBrowsingHistory': 'No browsing history found',
      // 'nav.vehicles': 'Buy Vehicles',
      // 'nav.property': 'Buy Property',
      // 'nav.vehiclesAndPets': 'Vehicles & pets',
      'nav.softwareCenter': 'Software center',
      'nav.softwareDigital': 'Buy Software & Digital Products',
      'nav.ebooks': 'Buy E-Books',
      'nav.becomeSeller': 'Become a Seller',

      // User Types
      'userType.b2b': 'B2B',
      'userType.b2c': 'B2C',

      // Authentication
      'auth.welcome': 'Welcome!',
      'auth.loginToContinue': 'Log in to Merchants Bazaar to continue.',
      'auth.login': 'Login',
      'auth.loggingIn': 'Logging in...',
      'auth.signup': 'Sign Up',
      'auth.email': 'Your email address',
      'auth.password': 'Your password',
      'auth.rememberMe': 'Remember me',
      'auth.forgotPassword': 'Forgot password?',
      'auth.loginWithGoogle': 'Log in with Google',
      'auth.loginWithApple': 'Log in with Apple',
      'auth.loginWithTwitter': 'Log in with X',
      'auth.dontHaveAccount': "Don't have an account?",
      'auth.customerAccount': 'Customer account',
      'auth.customerLoginTitle': 'Customer login',
      'auth.customerLoginSubtitle': 'Sign in to shop, track orders, and manage your wishlist.',
      'auth.customerSignupTitle': 'Create a customer account',
      'auth.customerSignupSubtitle': 'For shopping and orders — not for managing a seller store.',
      'auth.notSellerLogin': 'Are you a seller?',
      'auth.goToSellerLogin': 'Go to Seller login',
      'auth.notCustomerSignup': 'Want to sell on Merchants Bazaar?',
      'auth.goToSellerSignup': 'Create a seller account',
      'auth.customerPortalHint': 'Shop · Orders · Wishlist',
      'auth.sellerLoginShortcut': 'Seller login',

      // Common UI Elements
      'common.loading': 'Loading...',
      'common.error': 'Error',
      'common.success': 'Success',
      'common.save': 'Save',
      'common.cancel': 'Cancel',
      'common.edit': 'Edit',
      'common.delete': 'Delete',
      'common.view': 'View',
      'common.close': 'Close',
      'common.back': 'Back',
      'common.next': 'Next',
      'common.previous': 'Previous',
      'common.submit': 'Submit',
      'common.search': 'Search',
      'common.filter': 'Filter',
      'common.sort': 'Sort',
      'common.all': 'All',
      'common.select': 'Select',
      'common.required': 'Required',
      'common.optional': 'Optional',

      // Product UI (only static labels, not product data)
      'product.addToCart': 'Add to Cart',
      'product.buyNow': 'Buy Now',
      'product.price': 'Price',
      'product.originalPrice': 'Original Price',
      'product.discount': 'Discount',
      'product.inStock': 'In Stock',
      'product.outOfStock': 'Out of Stock',
      'product.quantity': 'Quantity',
      'product.description': 'Description',
      'product.specifications': 'Specifications',
      'product.reviews': 'Reviews',
      'product.rating': 'Rating',
      'product.recommended': 'Recommended Products',
      'product.related': 'Related Products',
      'home.recommendedProducts': 'Recommended Products',
      'home.browserHistory': 'Browser History',
      'home.products': 'Products',
      'home.viewAll': 'View all',
      'home.getProductsByCategory': 'Get products by Category',
      'home.exploreTitle': 'Explore MerchantsBazaar',
      'home.exploreShop': 'Shop',
      'home.exploreShopDesc': 'Browse all products from verified sellers. Free shipping, quality guaranteed.',
      'home.exploreCategories': 'Categories',
      'home.exploreCategoriesDesc': 'Electronics, Fashion, Home, Sports, Health, Baby & Kids, and more.',
      'home.exploreAIShopping': 'AI Shopping',
      'home.exploreAIShoppingDesc': 'Smart recommendations and chat. Find the best deals with AI.',
      'home.exploreVoiceShopping': 'Voice Shopping',
      'home.exploreVoiceShoppingDesc': 'Hands-free shopping. Say "Add to cart" or "Go to shop" with voice.',
      'home.exploreAuctions': 'Auctions',
      'home.exploreAuctionsDesc': 'Live auctions. Bid on products from verified sellers.',
      'home.heroTitle': 'Products, auctions, classifieds & AI shopping',
      'home.heroSubtitle':
        'Shop the catalog, bid in live auctions, browse vehicles, property and pets, and use AI assistants—all in one global marketplace.',
      'home.heroFeatures':
        'AI recommendations, voice and image search, immersive try-ons—plus classifieds for vehicles, homes, and pets.',
      'home.heroEyebrow': 'Global B2B & B2C marketplace',
      'home.heroPillShop': 'Shop catalog',
      'home.heroPillAuctions': 'Live auctions',
      'home.heroPillClassifieds': 'Classifieds',
      'home.heroPillAI': 'AI shopping',
      'home.heroTrustVerifiedTitle': 'Verified sellers',
      'home.heroTrustVerifiedDesc': 'Trusted merchants & reviews',
      'home.heroTrustSecureTitle': 'Secure payments',
      'home.heroTrustSecureDesc': 'Checkout with confidence',
      'home.heroTrustReachTitle': 'Worldwide reach',
      'home.heroTrustReachDesc': 'B2B and B2C in one place',

      // Shop and Filter UI
      'shop.clearAllFilters': 'Clear All Filters',
      'shop.category': 'Category',
      'shop.subCategory': 'Sub Category',
      'shop.brand': 'Brand',
      'shop.reviews': 'Reviews',
      'shop.sortOptions': 'Sort Options',
      'shop.supplierRegions': 'Supplier Regions',
      'shop.newArrivals': 'New arrivals',
      'shop.bestSellers': 'Best sellers',
      'shop.discounts': 'Discounts',
      'shop.sortBy': 'Sort by',
      'shop.price': 'Price',
      'shop.new': 'New Arrivals',
      'shop.ascending': 'Ascending',
      'shop.descending': 'Descending',
      'shop.openFilters': 'Open Filters',
      'shop.searchResults': 'Search Results',
      'shop.andUp': '& up',

      // Footer
      'footer.aboutText':
        'Merchants Bazaar lists products, live streams, auctions, and inventory from verified sellers. Shop with confidence, compare offers, and complete purchases according to each seller’s terms and local regulations.',
      'footer.colBrowse': 'Browse',
      'footer.colTools': 'Tools',
      'footer.colSell': 'Sell',
      'footer.colSupport': 'Support',
      'footer.newsletterTitle': 'Newsletter',
      'footer.newsletterAria': 'Newsletter signup',
      'footer.newsletterLead':
        'Occasional emails with product highlights, buyer tips, and Merchants Bazaar news.',
      'footer.newsletterHint':
        'We use your email only for Merchants Bazaar updates. You can unsubscribe anytime.',
      'footer.trustDisclaimer':
        'Listings and prices reflect live catalogue data from the Merchants Bazaar network. Verify details with the seller; use safer payment methods where offered; and report suspicious listings through Contact Us or customer support.',
      'footer.securePayment': 'Secure Payment',
      'footer.legalPolicies': 'Legal & Policies',
      'footer.termsConditions': 'Terms and Conditions',
      'footer.privacyPolicy': 'Privacy Policy',
      'footer.cookiesPolicy': 'Cookies Policy',
      'footer.cookiesPreferences': 'Cookies Preferences',
      'footer.shippingPolicies': 'Shipping Policies',
      'footer.quickLinks': 'Quick Links',
      'footer.blogNews': 'Blog & News',
      'footer.home': 'Home',
      'footer.products': 'Products',
      'footer.categories': 'Categories',
      'footer.aiShopping': 'AI Shopping',
      'footer.careers': 'Careers',
      'footer.about': 'About',
      'footer.contactUs': 'Contact Us',
      'footer.blog': 'Blog',
      'footer.news': 'News',
      'footer.dataManagement': 'Data Management',
      'footer.dataManagementTagline': 'Inventory, analytics & BI in one platform for sellers.',
      'footer.dataManagementUseApp': 'Use Data Management',
      'footer.dataManagementLearnMore': 'MerchantsBazaar Data Management platform',
      'footer.community': 'Merchants Bazaar Community',
      'footer.communityTagline': 'Spaces, discussions & updates for sellers and buyers on the marketplace.',
      'footer.communityVisit': 'Visit Community',
      'footer.subscribe': 'Subscribe',
      'footer.getUpdates': 'Get exclusive updates and offers.',
      'footer.emailPlaceholder': 'Your email',
      'footer.subscribeButton': 'Subscribe',
      'footer.copyright': 'All rights reserved.',
      'footer.pleaseEnterEmail': 'Please enter your email!',
      'footer.currency': 'Currency',
      'footer.country': 'Country',
      'footer.stayConnected': 'Stay Connected',
      'footer.followUs': 'Follow Us',
      'product.color': 'Color',
      'product.size': 'Size',
      'product.selectColor': 'Select Color',
      'product.selectSize': 'Select Size',

      // Categories
      'category.Electronics': 'Electronics',
      'category.Fashion and Apparel': 'Fashion and Apparel',
      'category.Beauty and Personal Care': 'Beauty and Personal Care',
      'category.Health and Wellness': 'Health and Wellness',
      'category.Home & Kitchen': 'Home & Kitchen',
      'category.Baby & Kids': 'Baby & Kids',
      'category.Automotive & Tools': 'Automotive & Tools',
      'category.Mobile & Accessories': 'Mobile & Accessories',
      'category.Computers & Accessories': 'Computers & Accessories',
      'category.Grocery & Food': 'Grocery & Food',
      'category.Sports & Outdoors': 'Sports & Outdoors',
      'category.Books & Stationery': 'Books & Stationery',
      'category.Gaming & Entertainment': 'Gaming & Entertainment',
      'category.Pet Supplies': 'Pet Supplies',
      'category.Jewelry & Watches': 'Jewelry & Watches',
      'category.Industrial & Scientific': 'Industrial & Scientific',
      'category.Bags, Wallets & Luggage': 'Bags, Wallets & Luggage',
      'category.Art & Craft Supplies': 'Art & Craft Supplies',

      // Subcategories - Electronics
      'subcategory.Televisions': 'Televisions',
      'subcategory.Cameras': 'Cameras',
      'subcategory.Audio Devices': 'Audio Devices',
      'subcategory.Electronic Machines': 'Electronic Machines',

      // Subcategories - Fashion and Apparel
      'subcategory.Men Clothing': 'Men Clothing',
      'subcategory.Women Clothing': 'Women Clothing',
      'subcategory.Shoes': 'Shoes',
      'subcategory.Accessories': 'Accessories',
      'subcategory.Watches': 'Watches',
      'subcategory.Kids Clothing': 'Kids Clothing',
      'subcategory.Ethnic Wear': 'Ethnic Wear',
      'subcategory.Innerwear': 'Innerwear',
      'subcategory.Eyewear': 'Eyewear',

      // Subcategories - Beauty and Personal Care
      'subcategory.Skin Care': 'Skin Care',
      'subcategory.Hair Care': 'Hair Care',
      'subcategory.Hair Color & Styling': 'Hair Color & Styling',
      'subcategory.Makeup': 'Makeup',
      'subcategory.Nail Care': 'Nail Care',
      'subcategory.Fragrances': 'Fragrances',
      'subcategory.Personal Hygiene': 'Personal Hygiene',
      "subcategory.Men's Grooming": "Men's Grooming",
      'subcategory.Beauty Tools': 'Beauty Tools',
      'subcategory.Oral Care': 'Oral Care',
      'subcategory.Bath and Body': 'Bath and Body',
      'subcategory.Sun Care': 'Sun Care',
      'subcategory.Hand & Foot Care': 'Hand & Foot Care',
      'subcategory.Feminine Care': 'Feminine Care',
      'subcategory.Essential Oils & Aromatherapy': 'Essential Oils & Aromatherapy',
      'subcategory.Beauty Sets & Kits': 'Beauty Sets & Kits',

      // Subcategories - Health and Wellness
      'subcategory.Supplements': 'Supplements',
      'subcategory.Fitness Equipment': 'Fitness Equipment',
      'subcategory.Medical Devices': 'Medical Devices',
      'subcategory.Health Drinks': 'Health Drinks',
      'subcategory.Essential Oils': 'Essential Oils',
      'subcategory.First Aid': 'First Aid',
      'subcategory.Pain Relief': 'Pain Relief',
      'subcategory.Mobility Aids': 'Mobility Aids',
      'subcategory.Mental Wellness': 'Mental Wellness',

      // Subcategories - Home & Kitchen
      'subcategory.Furniture': 'Furniture',
      'subcategory.Kitchenware': 'Kitchenware',
      'subcategory.Home Decor': 'Home Decor',
      'subcategory.Appliances': 'Appliances',
      'subcategory.Bedding & Bath': 'Bedding & Bath',
      'subcategory.Lighting': 'Lighting',
      'subcategory.Cleaning Supplies': 'Cleaning Supplies',
      'subcategory.Storage & Organization': 'Storage & Organization',
      'subcategory.Dining': 'Dining',
      'subcategory.Curtains & Blinds': 'Curtains & Blinds',

      // Cart & Checkout
      'cart.title': 'Shopping Cart',
      'cart.empty': 'Your cart is empty',
      'cart.remove': 'Remove',
      'cart.update': 'Update',
      'cart.subtotal': 'Subtotal',
      'cart.shipping': 'Shipping',
      'cart.tax': 'Tax',
      'cart.total': 'Total',
      'cart.checkout': 'Checkout',
      'cart.continueShopping': 'Continue Shopping',

      // Orders
      'orders.title': 'My Orders',
      'orders.orderId': 'Order ID',
      'orders.date': 'Date',
      'orders.status': 'Status',
      'orders.total': 'Total',
      'orders.viewDetails': 'View Details',
      'orders.trackOrder': 'Track Order',
      'orders.cancelOrder': 'Cancel Order',
      'orders.noOrders': 'No orders found',
      'orders.pending': 'Pending',
      'orders.processing': 'Processing',
      'orders.shipped': 'Shipped',
      'orders.delivered': 'Delivered',
      'orders.cancelled': 'Cancelled',

      // Profile
      'profile.title': 'My Profile',
      'profile.personalInfo': 'Personal Information',
      'profile.accountSettings': 'Account Settings',
      'profile.changePassword': 'Change Password',
      'profile.save': 'Save',
      'profile.cancel': 'Cancel',
      'profile.edit': 'Edit',

      // Favorites
      'favorites.title': 'My Favorites',
      'favorites.empty': 'No favorites yet',
      'favorites.addToCart': 'Add to Cart',
      'favorites.remove': 'Remove from Favorites',

      // Search
      'search.title': 'Search Results',
      'search.placeholder': 'Search products...',
      'search.noResults': 'No results found',
      'search.filters': 'Filters',
      'search.sortBy': 'Sort by',
      'search.clearFilters': 'Clear Filters',

      // Error Messages
      'error.network': 'Network error. Please check your connection.',
      'error.server': 'Server error. Please try again later.',
      'error.notFound': 'Page not found.',
      'error.unauthorized': 'Unauthorized access.',
      'error.validation': 'Please check your input.',
      'error.required': 'This field is required.',
      'error.email': 'Please enter a valid email.',
      'error.password': 'Password must be at least 6 characters.',
      'error.loginFailed': 'Login failed. Please check your credentials.',
      'error.cartEmpty': 'Your cart is empty.',

      // Success Messages
      'success.login': 'Login successful!',
      'success.signup': 'Account created successfully!',
      'success.profileUpdated': 'Profile updated successfully!',
      'success.addedToCart': 'Product added to cart!',
      'success.alreadyInCart': 'This product is already in your cart.',
      'success.removedFromCart': 'Product removed from cart!',
      'success.orderPlaced': 'Order placed successfully!',
    },

    ru: {
      // Navigation
      'nav.home': 'Главная',
      'nav.shop': 'Товары',
      'nav.about': 'О нас',
      'nav.contact': 'Контакты',
      'nav.login': 'Войти',
      'nav.signup': 'Регистрация',
      'nav.profile': 'Профиль',
      'nav.orders': 'Заказы',
      'nav.favorites': 'Избранное',
      'nav.cart': 'Корзина',
      'nav.logout': 'Выйти',
      'nav.language': 'Язык',
      'nav.search': 'Поиск',
      'nav.categories': 'Категории',
      'nav.deliverTo': 'Доставить в',
      'nav.policies': 'Политики',
      'nav.customerSupport': 'Поддержка клиентов',
      'nav.updatePassword': 'Изменить пароль',
      'nav.orderStatus': 'Статус заказа',
      'nav.browsingHistory': 'История просмотров',
      'nav.noBrowsingHistory': 'История просмотров не найдена',
      'nav.vehicles': 'Транспорт',
      'nav.property': 'Недвижимость',
      'nav.vehiclesAndPets': 'Транспорт и питомцы',
      'nav.softwareCenter': 'Центр ПО',
      'nav.softwareDigital': 'Купить ПО и цифровые товары',
      'nav.becomeSeller': 'Стать продавцом',

      // User Types
      'userType.b2b': 'B2B',
      'userType.b2c': 'B2C',

      // Authentication
      'auth.welcome': 'Добро пожаловать!',
      'auth.loginToContinue': 'Войдите в Merchants Bazaar, чтобы продолжить.',
      'auth.login': 'Войти',
      'auth.loggingIn': 'Вход...',
      'auth.signup': 'Регистрация',
      'auth.email': 'Ваш адрес электронной почты',
      'auth.password': 'Ваш пароль',
      'auth.rememberMe': 'Запомнить меня',
      'auth.forgotPassword': 'Забыли пароль?',
      'auth.loginWithGoogle': 'Войти через Google',
      'auth.loginWithApple': 'Войти через Apple',
      'auth.loginWithTwitter': 'Войти через X',
      'auth.dontHaveAccount': 'Нет аккаунта?',

      // Common UI Elements
      'common.loading': 'Загрузка...',
      'common.error': 'Ошибка',
      'common.success': 'Успех',
      'common.save': 'Сохранить',
      'common.cancel': 'Отмена',
      'common.edit': 'Редактировать',
      'common.delete': 'Удалить',
      'common.view': 'Просмотр',
      'common.close': 'Закрыть',
      'common.back': 'Назад',
      'common.next': 'Далее',
      'common.previous': 'Предыдущий',
      'common.submit': 'Отправить',
      'common.search': 'Поиск',
      'common.filter': 'Фильтр',
      'common.sort': 'Сортировать',
      'common.all': 'Все',
      'common.select': 'Выбрать',
      'common.required': 'Обязательно',
      'common.optional': 'Необязательно',

      // Product UI
      'product.addToCart': 'Добавить в корзину',
      'product.buyNow': 'Купить сейчас',
      'product.price': 'Цена',
      'product.originalPrice': 'Оригинальная цена',
      'product.discount': 'Скидка',
      'product.inStock': 'В наличии',
      'product.outOfStock': 'Нет в наличии',
      'product.quantity': 'Количество',
      'product.description': 'Описание',
      'product.specifications': 'Характеристики',
      'product.reviews': 'Отзывы',
      'product.rating': 'Рейтинг',
      'product.recommended': 'Рекомендуемые товары',
      'product.related': 'Похожие товары',
      'home.recommendedProducts': 'Рекомендуемые товары',
      'home.browserHistory': 'История просмотров',
      'home.products': 'Товары',
      'home.viewAll': 'Показать все',
      'home.getProductsByCategory': 'Получить товары по категории',
      'home.exploreTitle': 'Обзор MerchantsBazaar',
      'home.exploreShop': 'Магазин',
      'home.exploreShopDesc': 'Все товары от проверенных продавцов. Бесплатная доставка.',
      'home.exploreCategories': 'Категории',
      'home.exploreCategoriesDesc': 'Электроника, мода, дом, спорт, здоровье и другое.',
      'home.exploreAIShopping': 'AI покупки',
      'home.exploreAIShoppingDesc': 'Умные рекомендации и чат. Лучшие предложения с ИИ.',
      'home.exploreVoiceShopping': 'Голосовые покупки',
      'home.exploreVoiceShoppingDesc': 'Покупки без рук. Голосовые команды «В корзину», «В магазин».',
      'home.exploreAuctions': 'Аукционы',
      'home.exploreAuctionsDesc': 'Живые аукционы. Ставки от проверенных продавцов.',
      'home.heroTitle': 'Товары, аукционы, объявления и AI',
      'home.heroSubtitle':
        'Каталог, живые аукционы, авто, недвижимость и питомцы, плюс AI-помощники — всё на одной площадке.',
      'home.heroFeatures':
        'Рекомендации ИИ, голосовой и визуальный поиск, иммерсивные примерки — и объявления: авто, жильё, питомцы.',
      'home.heroEyebrow': 'Глобальный маркетплейс B2B и B2C',
      'home.heroPillShop': 'Каталог',
      'home.heroPillAuctions': 'Аукционы',
      'home.heroPillClassifieds': 'Объявления',
      'home.heroPillAI': 'AI-покупки',
      'home.heroTrustVerifiedTitle': 'Проверенные продавцы',
      'home.heroTrustVerifiedDesc': 'Надёжные магазины и отзывы',
      'home.heroTrustSecureTitle': 'Безопасная оплата',
      'home.heroTrustSecureDesc': 'Оформление без лишних рисков',
      'home.heroTrustReachTitle': 'По всему миру',
      'home.heroTrustReachDesc': 'B2B и B2C в одном месте',

      // Shop and Filter UI
      'shop.clearAllFilters': 'Очистить все фильтры',
      'shop.category': 'Категория',
      'shop.subCategory': 'Подкатегория',
      'shop.brand': 'Бренд',
      'shop.reviews': 'Отзывы',
      'shop.sortOptions': 'Параметры сортировки',
      'shop.supplierRegions': 'Регионы поставщиков',
      'shop.newArrivals': 'Новые поступления',
      'shop.bestSellers': 'Хиты продаж',
      'shop.discounts': 'Скидки',
      'shop.sortBy': 'Сортировать по',
      'shop.price': 'Цена',
      'shop.new': 'Новые поступления',
      'shop.ascending': 'По возрастанию',
      'shop.descending': 'По убыванию',
      'shop.openFilters': 'Открыть фильтры',
      'shop.searchResults': 'Результаты поиска',
      'shop.andUp': 'и выше',

      // Footer
      'footer.aboutText':
        'Мы предлагаем современные, минималистичные продукты с непревзойденной ценностью. Спасибо за покупки у нас.',
      'footer.securePayment': 'Безопасная оплата',
      'footer.legalPolicies': 'Правовые документы',
      'footer.termsConditions': 'Условия использования',
      'footer.privacyPolicy': 'Политика конфиденциальности',
      'footer.cookiesPolicy': 'Политика файлов cookie',
      'footer.cookiesPreferences': 'Настройки файлов cookie',
      'footer.shippingPolicies': 'Политика доставки',
      'footer.quickLinks': 'Быстрые ссылки',
      'footer.blogNews': 'Блог и Новости',
      'footer.home': 'Главная',
      'footer.products': 'Товары',
      'footer.categories': 'Категории',
      'footer.aiShopping': 'AI покупки',
      'footer.careers': 'Карьера',
      'footer.about': 'О нас',
      'footer.contactUs': 'Связаться с нами',
      'footer.blog': 'Блог',
      'footer.news': 'Новости',
      'footer.dataManagement': 'Управление данными',
      'footer.dataManagementTagline': 'Склад, аналитика и BI в одной платформе для продавцов.',
      'footer.dataManagementUseApp': 'Открыть Data Management',
      'footer.dataManagementLearnMore': 'Платформа Data Management MerchantsBazaar',
      'footer.community': 'Сообщество Merchants Bazaar',
      'footer.communityTagline': 'Пространства, обсуждения и новости для продавцов и покупателей.',
      'footer.communityVisit': 'Перейти в сообщество',
      'footer.subscribe': 'Подписаться',
      'footer.getUpdates': 'Получайте эксклюзивные обновления и предложения.',
      'footer.emailPlaceholder': 'Ваш email',
      'footer.subscribeButton': 'Подписаться',
      'footer.copyright': 'Все права защищены.',
      'footer.pleaseEnterEmail': 'Пожалуйста, введите ваш email!',
      'footer.currency': 'Валюта',
      'footer.country': 'Страна',
      'footer.stayConnected': 'Оставайтесь на связи',
      'footer.followUs': 'Подписывайтесь на нас',

      'product.color': 'Цвет',
      'product.size': 'Размер',
      'product.selectColor': 'Выберите цвет',
      'product.selectSize': 'Выберите размер',

      // Categories
      'category.Electronics': 'Электроника',
      'category.Fashion and Apparel': 'Мода и одежда',
      'category.Beauty and Personal Care': 'Красота и личная гигиена',
      'category.Health and Wellness': 'Здоровье и благополучие',
      'category.Home & Kitchen': 'Дом и кухня',
      'category.Baby & Kids': 'Дети и младенцы',
      'category.Automotive & Tools': 'Автомобили и инструменты',
      'category.Mobile & Accessories': 'Мобильные телефоны и аксессуары',
      'category.Computers & Accessories': 'Компьютеры и аксессуары',
      'category.Grocery & Food': 'Продукты питания',
      'category.Sports & Outdoors': 'Спорт и отдых на природе',
      'category.Books & Stationery': 'Книги и канцелярские товары',
      'category.Gaming & Entertainment': 'Игры и развлечения',
      'category.Pet Supplies': 'Товары для животных',
      'category.Jewelry & Watches': 'Украшения и часы',
      'category.Industrial & Scientific': 'Промышленные и научные товары',
      'category.Bags, Wallets & Luggage': 'Сумки, кошельки и багаж',
      'category.Art & Craft Supplies': 'Товары для творчества и рукоделия',

      // Subcategories - Electronics
      'subcategory.Televisions': 'Телевизоры',
      'subcategory.Cameras': 'Камеры',
      'subcategory.Audio Devices': 'Аудиоустройства',
      'subcategory.Electronic Machines': 'Электронные машины',

      // Subcategories - Fashion and Apparel
      'subcategory.Men Clothing': 'Мужская одежда',
      'subcategory.Women Clothing': 'Женская одежда',
      'subcategory.Shoes': 'Обувь',
      'subcategory.Accessories': 'Аксессуары',
      'subcategory.Watches': 'Часы',
      'subcategory.Kids Clothing': 'Детская одежда',
      'subcategory.Ethnic Wear': 'Этническая одежда',
      'subcategory.Innerwear': 'Нижнее белье',
      'subcategory.Eyewear': 'Очки',

      // Subcategories - Beauty and Personal Care
      'subcategory.Skin Care': 'Уход за кожей',
      'subcategory.Hair Care': 'Уход за волосами',
      'subcategory.Makeup': 'Макияж',
      'subcategory.Fragrances': 'Парфюмерия',
      'subcategory.Personal Hygiene': 'Личная гигиена',
      "subcategory.Men's Grooming": 'Мужской уход',
      'subcategory.Beauty Tools': 'Инструменты красоты',
      'subcategory.Oral Care': 'Уход за полостью рта',
      'subcategory.Bath and Body': 'Ванна и тело',

      // Subcategories - Health and Wellness
      'subcategory.Supplements': 'Добавки',
      'subcategory.Fitness Equipment': 'Фитнес-оборудование',
      'subcategory.Medical Devices': 'Медицинские устройства',
      'subcategory.Health Drinks': 'Здоровые напитки',
      'subcategory.Essential Oils': 'Эфирные масла',
      'subcategory.First Aid': 'Первая помощь',
      'subcategory.Pain Relief': 'Обезболивание',
      'subcategory.Mobility Aids': 'Средства передвижения',
      'subcategory.Mental Wellness': 'Психическое здоровье',

      // Subcategories - Home & Kitchen
      'subcategory.Furniture': 'Мебель',
      'subcategory.Kitchenware': 'Кухонная утварь',
      'subcategory.Home Decor': 'Домашний декор',
      'subcategory.Appliances': 'Бытовая техника',
      'subcategory.Bedding & Bath': 'Постельное белье и ванная',
      'subcategory.Lighting': 'Освещение',
      'subcategory.Cleaning Supplies': 'Чистящие средства',
      'subcategory.Storage & Organization': 'Хранение и организация',
      'subcategory.Dining': 'Столовая',
      'subcategory.Curtains & Blinds': 'Шторы и жалюзи',

      // Cart & Checkout
      'cart.title': 'Корзина покупок',
      'cart.empty': 'Ваша корзина пуста',
      'cart.remove': 'Удалить',
      'cart.update': 'Обновить',
      'cart.subtotal': 'Промежуточный итог',
      'cart.shipping': 'Доставка',
      'cart.tax': 'Налог',
      'cart.total': 'Итого',
      'cart.checkout': 'Оформить заказ',
      'cart.continueShopping': 'Продолжить покупки',

      // Orders
      'orders.title': 'Мои заказы',
      'orders.orderId': 'ID заказа',
      'orders.date': 'Дата',
      'orders.status': 'Статус',
      'orders.total': 'Итого',
      'orders.viewDetails': 'Просмотреть детали',
      'orders.trackOrder': 'Отследить заказ',
      'orders.cancelOrder': 'Отменить заказ',
      'orders.noOrders': 'Заказы не найдены',
      'orders.pending': 'Ожидает',
      'orders.processing': 'Обрабатывается',
      'orders.shipped': 'Отправлен',
      'orders.delivered': 'Доставлен',
      'orders.cancelled': 'Отменен',

      // Profile
      'profile.title': 'Мой профиль',
      'profile.personalInfo': 'Личная информация',
      'profile.accountSettings': 'Настройки аккаунта',
      'profile.changePassword': 'Изменить пароль',
      'profile.save': 'Сохранить',
      'profile.cancel': 'Отмена',
      'profile.edit': 'Редактировать',

      // Favorites
      'favorites.title': 'Мои избранные',
      'favorites.empty': 'Пока нет избранных',
      'favorites.addToCart': 'Добавить в корзину',
      'favorites.remove': 'Удалить из избранного',

      // Search
      'search.title': 'Результаты поиска',
      'search.placeholder': 'Поиск товаров...',
      'search.noResults': 'Результаты не найдены',
      'search.filters': 'Фильтры',
      'search.sortBy': 'Сортировать по',
      'search.clearFilters': 'Очистить фильтры',

      // Error Messages
      'error.network': 'Ошибка сети. Проверьте подключение.',
      'error.server': 'Ошибка сервера. Попробуйте позже.',
      'error.notFound': 'Страница не найдена.',
      'error.unauthorized': 'Неавторизованный доступ.',
      'error.validation': 'Проверьте введенные данные.',
      'error.required': 'Это поле обязательно.',
      'error.email': 'Введите корректный email.',
      'error.password': 'Пароль должен содержать минимум 6 символов.',
      'error.loginFailed': 'Ошибка входа. Проверьте данные.',
      'error.cartEmpty': 'Ваша корзина пуста.',

      // Success Messages
      'success.login': 'Вход выполнен успешно!',
      'success.signup': 'Аккаунт создан успешно!',
      'success.profileUpdated': 'Профиль обновлен успешно!',
      'success.addedToCart': 'Товар добавлен в корзину!',
      'success.removedFromCart': 'Товар удален из корзины!',
      'success.orderPlaced': 'Заказ размещен успешно!',
    },

    es: {
      // Navigation
      'nav.home': 'Inicio',
      'nav.shop': 'Productos',
      'nav.about': 'Acerca de',
      'nav.contact': 'Contacto',
      'nav.login': 'Iniciar sesión',
      'nav.signup': 'Registrarse',
      'nav.profile': 'Perfil',
      'nav.orders': 'Pedidos',
      'nav.favorites': 'Favoritos',
      'nav.cart': 'Carrito',
      'nav.logout': 'Cerrar sesión',
      'nav.language': 'Idioma',
      'nav.search': 'Buscar',
      'nav.categories': 'Categorías',
      'nav.deliverTo': 'Entregar a',
      'nav.policies': 'Políticas',
      'nav.customerSupport': 'Soporte al cliente',
      'nav.updatePassword': 'Actualizar contraseña',
      'nav.orderStatus': 'Estado del pedido',
      'nav.browsingHistory': 'Historial de navegación',
      'nav.noBrowsingHistory': 'No se encontró historial de navegación',
      'nav.vehicles': 'Comprar vehículos',
      'nav.property': 'Comprar inmuebles',
      'nav.vehiclesAndPets': 'Vehículos y mascotas',
      'nav.softwareCenter': 'Centro de software',
      'nav.softwareDigital': 'Comprar software y productos digitales',
      'nav.becomeSeller': 'Conviértete en vendedor',

      // User Types
      'userType.b2b': 'B2B',
      'userType.b2c': 'B2C',

      // Authentication
      'auth.welcome': '¡Bienvenido!',
      'auth.loginToContinue': 'Inicia sesión en Merchants Bazaar para continuar.',
      'auth.login': 'Iniciar sesión',
      'auth.loggingIn': 'Iniciando sesión...',
      'auth.signup': 'Registrarse',
      'auth.email': 'Tu dirección de correo electrónico',
      'auth.password': 'Tu contraseña',
      'auth.rememberMe': 'Recordarme',
      'auth.forgotPassword': '¿Olvidaste tu contraseña?',
      'auth.loginWithGoogle': 'Iniciar sesión con Google',
      'auth.loginWithApple': 'Iniciar sesión con Apple',
      'auth.loginWithTwitter': 'Iniciar sesión con X',
      'auth.dontHaveAccount': '¿No tienes una cuenta?',

      // Common UI Elements
      'common.loading': 'Cargando...',
      'common.error': 'Error',
      'common.success': 'Éxito',
      'common.save': 'Guardar',
      'common.cancel': 'Cancelar',
      'common.edit': 'Editar',
      'common.delete': 'Eliminar',
      'common.view': 'Ver',
      'common.close': 'Cerrar',
      'common.back': 'Atrás',
      'common.next': 'Siguiente',
      'common.previous': 'Anterior',
      'common.submit': 'Enviar',
      'common.search': 'Buscar',
      'common.filter': 'Filtrar',
      'common.sort': 'Ordenar',
      'common.all': 'Todos',
      'common.select': 'Seleccionar',
      'common.required': 'Requerido',
      'common.optional': 'Opcional',

      'product.description': 'Descripción',
      'product.specifications': 'Especificaciones',
      'product.reviews': 'Reseñas',
      'product.rating': 'Calificación',
      'product.recommended': 'Productos recomendados',
      'product.related': 'Productos relacionados',
      'home.recommendedProducts': 'Productos recomendados',
      'home.browserHistory': 'Historial de navegación',
      'home.products': 'Productos',
      'home.viewAll': 'Ver todo',
      'home.getProductsByCategory': 'Obtener productos por categoría',
      'home.exploreTitle': 'Explorar MerchantsBazaar',
      'home.exploreShop': 'Tienda',
      'home.exploreShopDesc': 'Todos los productos de vendedores verificados. Envío gratis.',
      'home.exploreCategories': 'Categorías',
      'home.exploreCategoriesDesc': 'Electrónica, moda, hogar, deportes, salud y más.',
      'home.exploreAIShopping': 'Compras con IA',
      'home.exploreAIShoppingDesc': 'Recomendaciones y chat. Encuentra ofertas con IA.',
      'home.exploreVoiceShopping': 'Compras por voz',
      'home.exploreVoiceShoppingDesc': 'Compras manos libres. Di "Añadir al carrito" o "Ir a tienda".',
      'home.exploreAuctions': 'Subastas',
      'home.exploreAuctionsDesc': 'Subastas en vivo. Puja por productos de vendedores verificados.',
      'home.heroTitle': 'Productos, subastas y anuncios',
      'home.heroSubtitle':
        'Compra tendencias, puja en subastas en vivo o explora vehículos e inmuebles, sin prisa. Tecnología, experiencia y escaparate en un solo lugar.',
      'home.heroFeatures':
        'Prueba compras en VR, con IA y por voz para una experiencia superior—más subastas y anuncios de vehículos e inmuebles.',
      'home.heroEyebrow': 'Mercado global B2B y B2C',
      'home.heroPillShop': 'Catálogo',
      'home.heroPillAuctions': 'Subastas',
      'home.heroPillClassifieds': 'Clasificados',
      'home.heroPillAI': 'Compra con IA',
      'home.heroTrustVerifiedTitle': 'Vendedores verificados',
      'home.heroTrustVerifiedDesc': 'Comercios de confianza y opiniones',
      'home.heroTrustSecureTitle': 'Pagos seguros',
      'home.heroTrustSecureDesc': 'Paga con tranquilidad',
      'home.heroTrustReachTitle': 'Alcance mundial',
      'home.heroTrustReachDesc': 'B2B y B2C en un solo lugar',

      // Shop and Filter UI
      'shop.clearAllFilters': 'Limpiar todos los filtros',
      'shop.category': 'Categoría',
      'shop.subCategory': 'Subcategoría',
      'shop.brand': 'Marca',
      'shop.reviews': 'Reseñas',
      'shop.sortOptions': 'Opciones de ordenamiento',
      'shop.supplierRegions': 'Regiones de proveedores',
      'shop.newArrivals': 'Nuevos productos',
      'shop.bestSellers': 'Más vendidos',
      'shop.discounts': 'Descuentos',
      'shop.sortBy': 'Ordenar por',
      'shop.price': 'Precio',
      'shop.new': 'Nuevos productos',
      'shop.ascending': 'Ascendente',
      'shop.descending': 'Descendente',
      'shop.openFilters': 'Abrir filtros',
      'shop.searchResults': 'Resultados de búsqueda',
      'shop.andUp': 'y más',

      // Footer
      'footer.aboutText':
        'Te traemos productos modernos y minimalistas con un valor inmejorable. Gracias por comprar con nosotros.',
      'footer.securePayment': 'Pago seguro',
      'footer.legalPolicies': 'Legales y políticas',
      'footer.termsConditions': 'Términos y condiciones',
      'footer.privacyPolicy': 'Política de privacidad',
      'footer.cookiesPolicy': 'Política de cookies',
      'footer.cookiesPreferences': 'Preferencias de cookies',
      'footer.shippingPolicies': 'Políticas de envío',
      'footer.quickLinks': 'Enlaces rápidos',
      'footer.blogNews': 'Blog y Noticias',
      'footer.home': 'Inicio',
      'footer.products': 'Productos',
      'footer.categories': 'Categorías',
      'footer.aiShopping': 'Compras con IA',
      'footer.careers': 'Carreras',
      'footer.about': 'Acerca de',
      'footer.contactUs': 'Contáctanos',
      'footer.blog': 'Blog',
      'footer.news': 'Noticias',
      'footer.dataManagement': 'Gestión de datos',
      'footer.dataManagementTagline': 'Inventario, análisis y BI en una plataforma para vendedores.',
      'footer.dataManagementUseApp': 'Usar Data Management',
      'footer.dataManagementLearnMore': 'Plataforma Data Management de MerchantsBazaar',
      'footer.community': 'Comunidad Merchants Bazaar',
      'footer.communityTagline': 'Espacios, debates y novedades para vendedores y compradores.',
      'footer.communityVisit': 'Ir a la comunidad',
      'footer.subscribe': 'Suscribirse',
      'footer.getUpdates': 'Obtén actualizaciones y ofertas exclusivas.',
      'footer.emailPlaceholder': 'Tu email',
      'footer.subscribeButton': 'Suscribirse',
      'footer.copyright': 'Todos los derechos reservados.',
      'footer.pleaseEnterEmail': '¡Por favor ingresa tu email!',
      'footer.currency': 'Moneda',
      'footer.country': 'País',
      'footer.stayConnected': 'Mantente Conectado',
      'footer.followUs': 'Síguenos',

      'product.color': 'Color',
      'product.size': 'Talla',
      'product.selectColor': 'Seleccionar color',
      'product.selectSize': 'Seleccionar talla',

      // Categories
      'category.Electronics': 'Electrónicos',
      'category.Fashion and Apparel': 'Moda y ropa',
      'category.Beauty and Personal Care': 'Belleza y cuidado personal',
      'category.Health and Wellness': 'Salud y bienestar',
      'category.Home & Kitchen': 'Hogar y cocina',
      'category.Baby & Kids': 'Bebés y niños',
      'category.Automotive & Tools': 'Automotriz y herramientas',
      'category.Mobile & Accessories': 'Móviles y accesorios',
      'category.Computers & Accessories': 'Computadoras y accesorios',
      'category.Grocery & Food': 'Comestibles y alimentos',
      'category.Sports & Outdoors': 'Deportes y actividades al aire libre',
      'category.Books & Stationery': 'Libros y papelería',
      'category.Gaming & Entertainment': 'Juegos y entretenimiento',
      'category.Pet Supplies': 'Suministros para mascotas',
      'category.Jewelry & Watches': 'Joyería y relojes',
      'category.Industrial & Scientific': 'Industrial y científico',
      'category.Bags, Wallets & Luggage': 'Bolsos, carteras y equipaje',
      'category.Art & Craft Supplies': 'Suministros de arte y manualidades',

      // Subcategories - Electronics
      'subcategory.Televisions': 'Televisores',
      'subcategory.Cameras': 'Cámaras',
      'subcategory.Audio Devices': 'Dispositivos de audio',
      'subcategory.Electronic Machines': 'Máquinas electrónicas',

      // Subcategories - Fashion and Apparel
      'subcategory.Men Clothing': 'Ropa de hombre',
      'subcategory.Women Clothing': 'Ropa de mujer',
      'subcategory.Shoes': 'Zapatos',
      'subcategory.Accessories': 'Accesorios',
      'subcategory.Watches': 'Relojes',
      'subcategory.Kids Clothing': 'Ropa de niños',
      'subcategory.Ethnic Wear': 'Ropa étnica',
      'subcategory.Innerwear': 'Ropa interior',
      'subcategory.Eyewear': 'Gafas',

      // Subcategories - Beauty and Personal Care
      'subcategory.Skin Care': 'Cuidado de la piel',
      'subcategory.Hair Care': 'Cuidado del cabello',
      'subcategory.Makeup': 'Maquillaje',
      'subcategory.Fragrances': 'Fragancias',
      'subcategory.Personal Hygiene': 'Higiene personal',
      "subcategory.Men's Grooming": 'Cuidado masculino',
      'subcategory.Beauty Tools': 'Herramientas de belleza',
      'subcategory.Oral Care': 'Cuidado bucal',
      'subcategory.Bath and Body': 'Baño y cuerpo',

      // Subcategories - Health and Wellness
      'subcategory.Supplements': 'Suplementos',
      'subcategory.Fitness Equipment': 'Equipamiento de fitness',
      'subcategory.Medical Devices': 'Dispositivos médicos',
      'subcategory.Health Drinks': 'Bebidas saludables',
      'subcategory.Essential Oils': 'Aceites esenciales',
      'subcategory.First Aid': 'Primeros auxilios',
      'subcategory.Pain Relief': 'Alivio del dolor',
      'subcategory.Mobility Aids': 'Ayudas para la movilidad',
      'subcategory.Mental Wellness': 'Bienestar mental',

      // Subcategories - Home & Kitchen
      'subcategory.Furniture': 'Muebles',
      'subcategory.Kitchenware': 'Utensilios de cocina',
      'subcategory.Home Decor': 'Decoración del hogar',
      'subcategory.Appliances': 'Electrodomésticos',
      'subcategory.Bedding & Bath': 'Ropa de cama y baño',
      'subcategory.Lighting': 'Iluminación',
      'subcategory.Cleaning Supplies': 'Suministros de limpieza',
      'subcategory.Storage & Organization': 'Almacenamiento y organización',
      'subcategory.Dining': 'Comedor',
      'subcategory.Curtains & Blinds': 'Cortinas y persianas',

      // Cart & Checkout
      'cart.title': 'Carrito de compras',
      'cart.empty': 'Tu carrito está vacío',
      'cart.remove': 'Eliminar',
      'cart.update': 'Actualizar',
      'cart.subtotal': 'Subtotal',
      'cart.shipping': 'Envío',
      'cart.tax': 'Impuesto',
      'cart.total': 'Total',
      'cart.checkout': 'Finalizar compra',
      'cart.continueShopping': 'Continuar comprando',

      // User Profile
      'profile.title': 'Mi perfil',
      'profile.personalInfo': 'Información personal',
      'profile.accountSettings': 'Configuración de cuenta',
      'profile.editProfile': 'Editar perfil',
      'profile.saveChanges': 'Guardar cambios',
      'profile.cancel': 'Cancelar',
      'profile.address': 'Dirección',
      'profile.addAddress': 'Agregar dirección',
      'profile.editAddress': 'Editar dirección',
      'profile.deleteAddress': 'Eliminar dirección',
      'profile.defaultAddress': 'Dirección predeterminada',
      'profile.orders': 'Mis pedidos',
      'profile.wishlist': 'Mi lista de deseos',
      'profile.reviews': 'Mis reseñas',

      // Orders
      'orders.title': 'Mis pedidos',
      'orders.orderId': 'ID del pedido',
      'orders.date': 'Fecha',
      'orders.status': 'Estado',
      'orders.total': 'Total',
      'orders.viewDetails': 'Ver detalles',
      'orders.trackOrder': 'Rastrear pedido',
      'orders.cancelOrder': 'Cancelar pedido',
      'orders.returnOrder': 'Devolver pedido',
      'orders.reorder': 'Volver a pedir',
      'orders.noOrders': 'No se encontraron pedidos',
      'orders.pending': 'Pendiente',
      'orders.confirmed': 'Confirmado',
      'orders.shipped': 'Enviado',
      'orders.delivered': 'Entregado',
      'orders.cancelled': 'Cancelado',
      'orders.returned': 'Devuelto',

      // Checkout
      'checkout.title': 'Finalizar compra',
      'checkout.shippingAddress': 'Dirección de envío',
      'checkout.billingAddress': 'Dirección de facturación',
      'checkout.paymentMethod': 'Método de pago',
      'checkout.orderSummary': 'Resumen del pedido',
      'checkout.placeOrder': 'Realizar pedido',
      'checkout.sameAsShipping': 'Igual que la dirección de envío',
      'checkout.creditCard': 'Tarjeta de crédito',
      'checkout.debitCard': 'Tarjeta de débito',
      'checkout.paypal': 'PayPal',
      'checkout.cashOnDelivery': 'Pago contra entrega',
      'checkout.cardNumber': 'Número de tarjeta',
      'checkout.expiryDate': 'Fecha de vencimiento',
      'checkout.cvv': 'CVV',
      'checkout.cardholderName': 'Nombre del titular',

      // Policies
      'policies.termsConditions': 'Términos y Condiciones',
      'policies.privacyPolicy': 'Política de Privacidad',
      'policies.cookiePolicy': 'Política de Cookies',
      'policies.cookiePreferences': 'Preferencias de Cookies',
      'policies.shippingPolicy': 'Política de Envío',
      'policies.returnPolicy': 'Política de Devolución',
      'policies.refundPolicy': 'Política de Reembolso',
      'policies.acceptAll': 'Aceptar todo',
      'policies.rejectAll': 'Rechazar todo',
      'policies.savePreferences': 'Guardar preferencias',
      'policies.necessary': 'Necesarias',
      'policies.analytics': 'Analíticas',
      'policies.marketing': 'Marketing',
      'policies.preferences': 'Preferencias',
      'policies.thirtyDayReturn': 'Devolución fácil de 30 días',
      'policies.notReturnable': 'No devolvable',

      // Contact & Support
      'contact.title': 'Contáctanos',
      'contact.name': 'Nombre',
      'contact.email': 'Correo electrónico',
      'contact.subject': 'Asunto',
      'contact.message': 'Mensaje',
      'contact.send': 'Enviar mensaje',
      'contact.phone': 'Teléfono',
      'contact.address': 'Dirección',
      'contact.businessHours': 'Horario de atención',
      'contact.support': 'Soporte al cliente',
      'contact.faq': 'Preguntas frecuentes',
      'contact.liveChat': 'Chat en vivo',
      'contact.ticket': 'Crear ticket de soporte',

      // Career
      'career.title': 'Carreras',
      'career.jobOpenings': 'Ofertas de trabajo',
      'career.applyNow': 'Aplicar ahora',
      'career.position': 'Posición',
      'career.location': 'Ubicación',
      'career.type': 'Tipo',
      'career.experience': 'Experiencia',
      'career.salary': 'Salario',
      'career.description': 'Descripción del trabajo',
      'career.requirements': 'Requisitos',
      'career.benefits': 'Beneficios',
      'career.fullTime': 'Tiempo completo',
      'career.partTime': 'Medio tiempo',
      'career.contract': 'Contrato',
      'career.remote': 'Remoto',

      // // Orders
      // 'orders.title': 'Mis pedidos',
      // 'orders.orderId': 'ID del pedido',
      // 'orders.date': 'Fecha',
      // 'orders.status': 'Estado',
      // 'orders.total': 'Total',
      // 'orders.viewDetails': 'Ver detalles',
      // 'orders.trackOrder': 'Rastrear pedido',
      // 'orders.cancelOrder': 'Cancelar pedido',
      // 'orders.noOrders': 'No se encontraron pedidos',
      // 'orders.pending': 'Pendiente',
      // 'orders.processing': 'Procesando',
      // 'orders.shipped': 'Enviado',
      // 'orders.delivered': 'Entregado',
      // 'orders.cancelled': 'Cancelado',

      // // Profile
      // 'profile.title': 'Mi perfil',
      // 'profile.personalInfo': 'Información personal',
      // 'profile.accountSettings': 'Configuración de cuenta',
      // 'profile.changePassword': 'Cambiar contraseña',
      // 'profile.save': 'Guardar',
      // 'profile.cancel': 'Cancelar',
      'profile.edit': 'Editar',

      // Favorites
      'favorites.title': 'Mis favoritos',
      'favorites.empty': 'Aún no tienes favoritos',
      'favorites.addToCart': 'Agregar al carrito',
      'favorites.remove': 'Eliminar de favoritos',

      // Search
      'search.title': 'Resultados de búsqueda',
      'search.placeholder': 'Buscar productos...',
      'search.noResults': 'No se encontraron resultados',
      'search.filters': 'Filtros',
      'search.sortBy': 'Ordenar por',
      'search.clearFilters': 'Limpiar filtros',

      // Error Messages
      'error.network': 'Error de red. Verifica tu conexión.',
      'error.server': 'Error del servidor. Inténtalo más tarde.',
      'error.notFound': 'Página no encontrada.',
      'error.unauthorized': 'Acceso no autorizado.',
      'error.validation': 'Por favor verifica tu entrada.',
      'error.required': 'Este campo es requerido.',
      'error.email': 'Por favor ingresa un email válido.',
      'error.password': 'La contraseña debe tener al menos 6 caracteres.',
      'error.loginFailed': 'Error al iniciar sesión. Verifica tus credenciales.',
      'error.cartEmpty': 'Tu carrito está vacío.',

      // Success Messages
      'success.login': '¡Inicio de sesión exitoso!',
      'success.signup': '¡Cuenta creada exitosamente!',
      'success.profileUpdated': '¡Perfil actualizado exitosamente!',
      'success.addedToCart': '¡Producto agregado al carrito!',
      'success.removedFromCart': '¡Producto eliminado del carrito!',
      'success.orderPlaced': '¡Pedido realizado exitosamente!',
    },

    ar: {
      // Navigation
      'nav.home': 'الرئيسية',
      'nav.shop': 'المنتجات',
      'nav.about': 'من نحن',
      'nav.contact': 'اتصل بنا',
      'nav.login': 'تسجيل الدخول',
      'nav.signup': 'إنشاء حساب',
      'nav.profile': 'الملف الشخصي',
      'nav.orders': 'الطلبات',
      'nav.favorites': 'المفضلة',
      'nav.cart': 'السلة',
      'nav.logout': 'تسجيل الخروج',
      'nav.language': 'اللغة',
      'nav.search': 'البحث',
      'nav.categories': 'الفئات',
      'nav.deliverTo': 'التوصيل إلى',
      'nav.policies': 'السياسات',
      'nav.customerSupport': 'دعم العملاء',
      'nav.updatePassword': 'تحديث كلمة المرور',
      'nav.orderStatus': 'حالة الطلب',
      'nav.browsingHistory': 'تاريخ التصفح',
      'nav.noBrowsingHistory': 'لا يوجد تاريخ تصفح',
      'nav.vehicles': 'شراء مركبات',
      'nav.property': 'شراء عقار',
      'nav.vehiclesAndPets': 'مركبات وحيوانات أليفة',
      'nav.softwareCenter': 'مركز البرمجيات',
      'nav.softwareDigital': 'شراء البرمجيات والمنتجات الرقمية',
      'nav.becomeSeller': 'كن بائعاً',

      // User Types
      'userType.b2b': 'B2B',
      'userType.b2c': 'B2C',

      // Authentication
      'auth.welcome': 'مرحباً!',
      'auth.loginToContinue': 'سجل دخولك إلى Merchants Bazaar للمتابعة.',
      'auth.login': 'تسجيل الدخول',
      'auth.loggingIn': 'جاري تسجيل الدخول...',
      'auth.signup': 'إنشاء حساب',
      'auth.email': 'عنوان بريدك الإلكتروني',
      'auth.password': 'كلمة المرور',
      'auth.rememberMe': 'تذكرني',
      'auth.forgotPassword': 'نسيت كلمة المرور؟',
      'auth.loginWithGoogle': 'تسجيل الدخول عبر Google',
      'auth.loginWithApple': 'تسجيل الدخول عبر Apple',
      'auth.loginWithTwitter': 'تسجيل الدخول عبر X',
      'auth.dontHaveAccount': 'ليس لديك حساب؟',

      // Common UI Elements
      'common.loading': 'جاري التحميل...',
      'common.error': 'خطأ',
      'common.success': 'نجح',
      'common.save': 'حفظ',
      'common.cancel': 'إلغاء',
      'common.edit': 'تعديل',
      'common.delete': 'حذف',
      'common.view': 'عرض',
      'common.close': 'إغلاق',
      'common.back': 'رجوع',
      'common.next': 'التالي',
      'common.previous': 'السابق',
      'common.submit': 'إرسال',
      'common.search': 'بحث',
      'common.filter': 'تصفية',
      'common.sort': 'ترتيب',
      'common.all': 'الكل',
      'common.select': 'اختر',
      'common.required': 'مطلوب',
      'common.optional': 'اختياري',

      'product.related': 'المنتجات ذات الصلة',
      'home.recommendedProducts': 'المنتجات الموصى بها',
      'home.browserHistory': 'تاريخ التصفح',
      'home.products': 'المنتجات',
      'home.viewAll': 'عرض الكل',
      'home.getProductsByCategory': 'احصل على المنتجات حسب الفئة',
      'home.exploreTitle': 'استكشف MerchantsBazaar',
      'home.exploreShop': 'متجر',
      'home.exploreShopDesc': 'جميع المنتجات من بائعين موثقين. شحن مجاني.',
      'home.exploreCategories': 'الفئات',
      'home.exploreCategoriesDesc': 'إلكترونيات، أزياء، منزل، رياضة، صحة والمزيد.',
      'home.exploreAIShopping': 'تسوق بالذكاء الاصطناعي',
      'home.exploreAIShoppingDesc': 'توصيات وردود ذكية. أفضل العروض بالذكاء الاصطناعي.',
      'home.exploreVoiceShopping': 'التسوق الصوتي',
      'home.exploreVoiceShoppingDesc': 'تسوق بدون استخدام اليدين. أوامر صوتية للتسوق.',
      'home.exploreAuctions': 'المزادات',
      'home.exploreAuctionsDesc': 'مزادات مباشرة. مزايدة من بائعين موثقين.',
      'home.heroTitle': 'منتجات، مزادات، إعلانات وتسوّق بالذكاء الاصطناعي',
      'home.heroSubtitle':
        'تسوّق الكتالوج، زايد في مزادات مباشرة، تصفّح المركبات والعقارات والحيوانات الأليفة، واستخدم مساعدات الذكاء الاصطناعي—كل ذلك في سوق واحد.',
      'home.heroFeatures':
        'توصيات ذكاء اصطناعي، بحث صوتي وبالصور، وتجارب غامرة—إضافةً لإعلانات المركبات والمنازل والحيوانات الأليفة.',
      'home.heroEyebrow': 'سوق عالمي B2B وB2C',
      'home.heroPillShop': 'المتجر',
      'home.heroPillAuctions': 'المزادات',
      'home.heroPillClassifieds': 'الإعلانات',
      'home.heroPillAI': 'تسوق بالذكاء الاصطناعي',
      'home.heroTrustVerifiedTitle': 'بائعون موثوقون',
      'home.heroTrustVerifiedDesc': 'تجار معتمدون وتقييمات',
      'home.heroTrustSecureTitle': 'دفع آمن',
      'home.heroTrustSecureDesc': 'إتمام الشراء بثقة',
      'home.heroTrustReachTitle': 'انتشار عالمي',
      'home.heroTrustReachDesc': 'B2B وB2C في منصة واحدة',

      // Shop and Filter UI
      'shop.clearAllFilters': 'مسح جميع المرشحات',
      'shop.category': 'الفئة',
      'shop.subCategory': 'الفئة الفرعية',
      'shop.brand': 'العلامة التجارية',
      'shop.reviews': 'التقييمات',
      'shop.sortOptions': 'خيارات الترتيب',
      'shop.supplierRegions': 'مناطق الموردين',
      'shop.newArrivals': 'الوافدات الجديدة',
      'shop.bestSellers': 'الأكثر مبيعاً',
      'shop.discounts': 'الخصومات',
      'shop.sortBy': 'ترتيب حسب',
      'shop.price': 'السعر',
      'shop.new': 'الوافدات الجديدة',
      'shop.ascending': 'تصاعدي',
      'shop.descending': 'تنازلي',
      'shop.openFilters': 'فتح المرشحات',
      'shop.searchResults': 'نتائج البحث',
      'shop.andUp': 'وأعلى',

      // Footer
      'footer.aboutText': 'نحن نقدم لك منتجات حديثة ومبسطة بقيمة لا تُقارن. شكراً لكم للتسوق معنا.',
      'footer.securePayment': 'دفع آمن',
      'footer.legalPolicies': 'السياسات القانونية',
      'footer.termsConditions': 'الشروط والأحكام',
      'footer.privacyPolicy': 'سياسة الخصوصية',
      'footer.cookiesPolicy': 'سياسة ملفات تعريف الارتباط',
      'footer.cookiesPreferences': 'تفضيلات ملفات تعريف الارتباط',
      'footer.shippingPolicies': 'سياسات الشحن',
      'footer.quickLinks': 'روابط سريعة',
      'footer.blogNews': 'المدونة والأخبار',
      'footer.home': 'الرئيسية',
      'footer.products': 'المنتجات',
      'footer.categories': 'الفئات',
      'footer.aiShopping': 'تسوق بالذكاء الاصطناعي',
      'footer.careers': 'الوظائف',
      'footer.about': 'حولنا',
      'footer.contactUs': 'اتصل بنا',
      'footer.blog': 'المدونة',
      'footer.news': 'الأخبار',
      'footer.dataManagement': 'إدارة البيانات',
      'footer.dataManagementTagline': 'المخزون والتحليلات وBI في منصة واحدة للبائعين.',
      'footer.dataManagementUseApp': 'استخدام Data Management',
      'footer.dataManagementLearnMore': 'منصة إدارة البيانات MerchantsBazaar',
      'footer.community': 'مجتمع Merchants Bazaar',
      'footer.communityTagline': 'مساحات ومناقشات وتحديثات للبائعين والمشترين.',
      'footer.communityVisit': 'زيارة المجتمع',
      'footer.subscribe': 'اشترك',
      'footer.getUpdates': 'احصل على التحديثات والعروض الحصرية.',
      'footer.emailPlaceholder': 'بريدك الإلكتروني',
      'footer.subscribeButton': 'اشترك',
      'footer.copyright': 'جميع الحقوق محفوظة.',
      'footer.pleaseEnterEmail': 'يرجى إدخال بريدك الإلكتروني!',
      'footer.currency': 'العملة',
      'footer.country': 'البلد',
      'footer.stayConnected': 'ابق على اتصال',
      'footer.followUs': 'تابعنا',

      'product.color': 'اللون',
      'product.size': 'الحجم',
      'product.selectColor': 'اختر اللون',
      'product.selectSize': 'اختر الحجم',

      // Categories
      'category.Electronics': 'الإلكترونيات',
      'category.Fashion and Apparel': 'الموضة والملابس',
      'category.Beauty and Personal Care': 'الجمال والعناية الشخصية',
      'category.Health and Wellness': 'الصحة والعافية',
      'category.Home & Kitchen': 'المنزل والمطبخ',
      'category.Baby & Kids': 'الأطفال والرضع',
      'category.Automotive & Tools': 'السيارات والأدوات',
      'category.Mobile & Accessories': 'الهواتف المحمولة والإكسسوارات',
      'category.Computers & Accessories': 'أجهزة الكمبيوتر والإكسسوارات',
      'category.Grocery & Food': 'البقالة والطعام',
      'category.Sports & Outdoors': 'الرياضة والأنشطة الخارجية',
      'category.Books & Stationery': 'الكتب والقرطاسية',
      'category.Gaming & Entertainment': 'الألعاب والترفيه',
      'category.Pet Supplies': 'مستلزمات الحيوانات الأليفة',
      'category.Jewelry & Watches': 'المجوهرات والساعات',
      'category.Industrial & Scientific': 'الصناعي والعلمي',
      'category.Bags, Wallets & Luggage': 'الحقائب والمحافظ والأمتعة',
      'category.Art & Craft Supplies': 'مستلزمات الفن والحرف اليدوية',

      // Subcategories - Electronics
      'subcategory.Televisions': 'التلفزيونات',
      'subcategory.Cameras': 'الكاميرات',
      'subcategory.Audio Devices': 'أجهزة الصوت',
      'subcategory.Electronic Machines': 'الآلات الإلكترونية',

      // Subcategories - Fashion and Apparel
      'subcategory.Men Clothing': 'ملابس رجالية',
      'subcategory.Women Clothing': 'ملابس نسائية',
      'subcategory.Shoes': 'أحذية',
      'subcategory.Accessories': 'إكسسوارات',
      'subcategory.Watches': 'ساعات',
      'subcategory.Kids Clothing': 'ملابس أطفال',
      'subcategory.Ethnic Wear': 'ملابس تقليدية',
      'subcategory.Innerwear': 'ملابس داخلية',
      'subcategory.Eyewear': 'نظارات',

      // Subcategories - Beauty and Personal Care
      'subcategory.Skin Care': 'العناية بالبشرة',
      'subcategory.Hair Care': 'العناية بالشعر',
      'subcategory.Makeup': 'مكياج',
      'subcategory.Fragrances': 'عطور',
      'subcategory.Personal Hygiene': 'النظافة الشخصية',
      "subcategory.Men's Grooming": 'العناية الرجالية',
      'subcategory.Beauty Tools': 'أدوات الجمال',
      'subcategory.Oral Care': 'العناية بالفم',
      'subcategory.Bath and Body': 'الحمام والجسم',

      // Subcategories - Health and Wellness
      'subcategory.Supplements': 'مكملات غذائية',
      'subcategory.Fitness Equipment': 'معدات اللياقة البدنية',
      'subcategory.Medical Devices': 'أجهزة طبية',
      'subcategory.Health Drinks': 'مشروبات صحية',
      'subcategory.Essential Oils': 'زيوت عطرية',
      'subcategory.First Aid': 'الإسعافات الأولية',
      'subcategory.Pain Relief': 'تسكين الآلام',
      'subcategory.Mobility Aids': 'مساعدات الحركة',
      'subcategory.Mental Wellness': 'الصحة النفسية',

      // Subcategories - Home & Kitchen
      'subcategory.Furniture': 'أثاث',
      'subcategory.Kitchenware': 'أدوات المطبخ',
      'subcategory.Home Decor': 'ديكور المنزل',
      'subcategory.Appliances': 'أجهزة منزلية',
      'subcategory.Bedding & Bath': 'أغطية السرير والحمام',
      'subcategory.Lighting': 'إضاءة',
      'subcategory.Cleaning Supplies': 'مستلزمات التنظيف',
      'subcategory.Storage & Organization': 'التخزين والتنظيم',
      'subcategory.Dining': 'طعام',
      'subcategory.Curtains & Blinds': 'ستائر ومظلات',

      // Cart & Checkout
      'cart.title': 'سلة التسوق',
      'cart.empty': 'سلتك فارغة',
      'cart.remove': 'إزالة',
      'cart.update': 'تحديث',
      'cart.subtotal': 'المجموع الفرعي',
      'cart.shipping': 'الشحن',
      'cart.tax': 'الضريبة',
      'cart.total': 'المجموع',
      'cart.checkout': 'الدفع',
      'cart.continueShopping': 'متابعة التسوق',
    },
    de: {
      // Navigation
      'nav.home': 'Startseite',
      'nav.shop': 'Produkte',
      'nav.about': 'Über uns',
      'nav.contact': 'Kontakt',
      'nav.login': 'Anmelden',
      'nav.signup': 'Registrieren',
      'nav.profile': 'Profil',
      'nav.orders': 'Bestellungen',
      'nav.favorites': 'Favoriten',
      'nav.cart': 'Warenkorb',
      'nav.logout': 'Abmelden',
      'nav.language': 'Sprache',
      'nav.search': 'Suchen',
      'nav.categories': 'Kategorien',
      'nav.deliverTo': 'Liefern an',
      'nav.policies': 'Richtlinien',
      'nav.customerSupport': 'Kundensupport',
      'nav.updatePassword': 'Passwort aktualisieren',
      'nav.orderStatus': 'Bestellstatus',
      'nav.browsingHistory': 'Verlauf',
      'nav.noBrowsingHistory': 'Kein Verlauf gefunden',
      'nav.vehicles': 'Fahrzeuge kaufen',
      'nav.property': 'Immobilien kaufen',
      'nav.vehiclesAndPets': 'Fahrzeuge & Haustiere',
      'nav.softwareCenter': 'Software-Center',
      'nav.softwareDigital': 'Software & digitale Produkte kaufen',
      'nav.becomeSeller': 'Verkäufer werden',

      // User Types
      'userType.b2b': 'B2B',
      'userType.b2c': 'B2C',

      // Authentication
      'auth.welcome': 'Willkommen!',
      'auth.loginToContinue': 'Melden Sie sich bei Merchants Bazaar an, um fortzufahren.',
      'auth.login': 'Anmelden',
      'auth.loggingIn': 'Anmeldung läuft...',
      'auth.signup': 'Registrieren',
      'auth.email': 'Ihre E-Mail-Adresse',
      'auth.password': 'Ihr Passwort',
      'auth.rememberMe': 'Angemeldet bleiben',
      'auth.forgotPassword': 'Passwort vergessen?',
      'auth.loginWithGoogle': 'Mit Google anmelden',
      'auth.loginWithApple': 'Mit Apple anmelden',
      'auth.loginWithTwitter': 'Mit X anmelden',
      'auth.dontHaveAccount': 'Noch kein Konto?',

      // Common UI Elements
      'common.loading': 'Lädt...',
      'common.error': 'Fehler',
      'common.success': 'Erfolg',
      'common.save': 'Speichern',
      'common.cancel': 'Abbrechen',
      'common.edit': 'Bearbeiten',
      'common.delete': 'Löschen',
      'common.view': 'Ansehen',
      'common.close': 'Schließen',
      'common.back': 'Zurück',
      'common.next': 'Weiter',
      'common.previous': 'Zurück',
      'common.submit': 'Absenden',
      'common.search': 'Suchen',
      'common.filter': 'Filtern',
      'common.sort': 'Sortieren',
      'common.all': 'Alle',
      'common.select': 'Auswählen',
      'common.required': 'Erforderlich',
      'common.optional': 'Optional',

      // Product UI
      'product.addToCart': 'In den Warenkorb',
      'product.buyNow': 'Jetzt kaufen',
      'product.price': 'Preis',
      'product.originalPrice': 'Ursprünglicher Preis',
      'product.discount': 'Rabatt',
      'product.inStock': 'Auf Lager',
      'product.outOfStock': 'Nicht vorrätig',
      'product.quantity': 'Menge',
      'product.description': 'Beschreibung',
      'product.specifications': 'Spezifikationen',
      'product.reviews': 'Bewertungen',
      'product.rating': 'Bewertung',
      'product.recommended': 'Empfohlene Produkte',
      'product.related': 'Ähnliche Produkte',
      'product.color': 'Farbe',
      'product.size': 'Größe',
      'product.selectColor': 'Farbe auswählen',
      'product.selectSize': 'Größe auswählen',

      // Home
      'home.recommendedProducts': 'Empfohlene Produkte',
      'home.browserHistory': 'Verlauf',
      'home.products': 'Produkte',
      'home.viewAll': 'Alle anzeigen',
      'home.getProductsByCategory': 'Produkte nach Kategorie abrufen',
      'home.exploreTitle': 'MerchantsBazaar entdecken',
      'home.exploreShop': 'Shop',
      'home.exploreShopDesc': 'Alle Produkte von verifizierten Verkäufern. Kostenloser Versand.',
      'home.exploreCategories': 'Kategorien',
      'home.exploreCategoriesDesc': 'Elektronik, Mode, Zuhause, Sport, Gesundheit und mehr.',
      'home.exploreAIShopping': 'KI-Shopping',
      'home.exploreAIShoppingDesc': 'Smarte Empfehlungen und Chat. Beste Deals mit KI.',
      'home.exploreVoiceShopping': 'Sprach-Shopping',
      'home.exploreVoiceShoppingDesc': 'Hands-free einkaufen. „In den Warenkorb“ oder „Zum Shop“.',
      'home.exploreAuctions': 'Auktionen',
      'home.exploreAuctionsDesc': 'Live-Auktionen. Bieten Sie bei verifizierten Verkäufern.',
      'home.heroTitle': 'Produkte, Auktionen, Kleinanzeigen & KI-Shopping',
      'home.heroSubtitle':
        'Katalog, Live-Auktionen, Fahrzeuge, Immobilien und Haustiere—dazu KI-Assistenten auf einem globalen Marktplatz.',
      'home.heroFeatures':
        'KI-Empfehlungen, Sprach- und Bildsuche, immersive Anprobe—plus Inserate für Fahrzeuge, Wohnen und Haustiere.',
      'home.heroEyebrow': 'Globaler B2B- & B2C-Marktplatz',
      'home.heroPillShop': 'Shop-Katalog',
      'home.heroPillAuctions': 'Live-Auktionen',
      'home.heroPillClassifieds': 'Kleinanzeigen',
      'home.heroPillAI': 'KI-Shopping',
      'home.heroTrustVerifiedTitle': 'Geprüfte Verkäufer',
      'home.heroTrustVerifiedDesc': 'Vertrauenswürdige Händler & Bewertungen',
      'home.heroTrustSecureTitle': 'Sichere Zahlung',
      'home.heroTrustSecureDesc': 'Sorgenfrei zur Kasse',
      'home.heroTrustReachTitle': 'Weltweit',
      'home.heroTrustReachDesc': 'B2B und B2C an einem Ort',

      // Shop and Filter UI
      'shop.clearAllFilters': 'Alle Filter löschen',
      'shop.category': 'Kategorie',
      'shop.subCategory': 'Unterkategorie',
      'shop.brand': 'Marke',
      'shop.reviews': 'Bewertungen',
      'shop.sortOptions': 'Sortieroptionen',
      'shop.supplierRegions': 'Lieferantenregionen',
      'shop.newArrivals': 'Neuheiten',
      'shop.bestSellers': 'Bestseller',
      'shop.discounts': 'Rabatte',
      'shop.sortBy': 'Sortieren nach',
      'shop.price': 'Preis',
      'shop.new': 'Neuheiten',
      'shop.ascending': 'Aufsteigend',
      'shop.descending': 'Absteigend',
      'shop.openFilters': 'Filter öffnen',
      'shop.searchResults': 'Suchergebnisse',
      'shop.andUp': 'und mehr',

      // Footer
      'footer.aboutText': 'Wir bieten Ihnen moderne und minimalistische Produkte mit unschlagbarem Wert. Vielen Dank für Ihren Einkauf bei uns.',
      'footer.securePayment': 'Sichere Zahlung',
      'footer.legalPolicies': 'Rechtliches und Richtlinien',
      'footer.termsConditions': 'Allgemeine Geschäftsbedingungen',
      'footer.privacyPolicy': 'Datenschutzerklärung',
      'footer.cookiesPolicy': 'Cookie-Richtlinie',
      'footer.cookiesPreferences': 'Cookie-Einstellungen',
      'footer.shippingPolicies': 'Versandrichtlinien',
      'footer.quickLinks': 'Schnelllinks',
      'footer.blogNews': 'Blog und Nachrichten',
      'footer.home': 'Startseite',
      'footer.products': 'Produkte',
      'footer.categories': 'Kategorien',
      'footer.aiShopping': 'KI-Shopping',
      'footer.careers': 'Karriere',
      'footer.about': 'Über uns',
      'footer.contactUs': 'Kontakt',
      'footer.blog': 'Blog',
      'footer.news': 'Nachrichten',
      'footer.dataManagement': 'Datenverwaltung',
      'footer.dataManagementTagline': 'Inventar, Analysen & BI in einer Plattform für Verkäufer.',
      'footer.dataManagementUseApp': 'Data Management nutzen',
      'footer.dataManagementLearnMore': 'MerchantsBazaar Data-Management-Plattform',
      'footer.community': 'Merchants Bazaar Community',
      'footer.communityTagline': 'Bereiche, Diskussionen und Updates für Verkäufer und Käufer.',
      'footer.communityVisit': 'Zur Community',
      'footer.subscribe': 'Abonnieren',
      'footer.getUpdates': 'Erhalten Sie Updates und exklusive Angebote.',
      'footer.emailPlaceholder': 'Ihre E-Mail',
      'footer.subscribeButton': 'Abonnieren',
      'footer.copyright': 'Alle Rechte vorbehalten.',
      'footer.pleaseEnterEmail': 'Bitte geben Sie Ihre E-Mail ein!',
      'footer.currency': 'Währung',
      'footer.country': 'Land',
      'footer.stayConnected': 'Bleiben Sie verbunden',
      'footer.followUs': 'Folgen Sie uns',

      // Cart & Checkout
      'cart.title': 'Warenkorb',
      'cart.empty': 'Ihr Warenkorb ist leer',
      'cart.remove': 'Entfernen',
      'cart.update': 'Aktualisieren',
      'cart.subtotal': 'Zwischensumme',
      'cart.shipping': 'Versand',
      'cart.tax': 'Steuer',
      'cart.total': 'Gesamt',
      'cart.checkout': 'Zur Kasse',
      'cart.continueShopping': 'Weiter einkaufen',

      // Categories
      'category.Electronics': 'Elektronik',
      'category.Fashion and Apparel': 'Mode und Bekleidung',
      'category.Beauty and Personal Care': 'Schönheit und Körperpflege',
      'category.Health and Wellness': 'Gesundheit und Wellness',
      'category.Home & Kitchen': 'Haushalt & Küche',
      'category.Baby & Kids': 'Baby & Kinder',
      'category.Automotive & Tools': 'Automobil & Werkzeuge',
      'category.Mobile & Accessories': 'Handy & Zubehör',
      'category.Computers & Accessories': 'Computer & Zubehör',
      'category.Grocery & Food': 'Lebensmittel',
      'category.Sports & Outdoors': 'Sport & Outdoor',
      'category.Books & Stationery': 'Bücher & Schreibwaren',
      'category.Gaming & Entertainment': 'Gaming & Unterhaltung',
      'category.Pet Supplies': 'Haustierzubehör',
      'category.Jewelry & Watches': 'Schmuck & Uhren',
      'category.Industrial & Scientific': 'Industrie & Wissenschaft',
      'category.Bags, Wallets & Luggage': 'Taschen, Geldbörsen & Gepäck',
      'category.Art & Craft Supplies': 'Kunst- & Bastelbedarf',
    },
    nl: {
      // Navigation
      'nav.home': 'Home',
      'nav.shop': 'Producten',
      'nav.about': 'Over ons',
      'nav.contact': 'Contact',
      'nav.login': 'Inloggen',
      'nav.signup': 'Registreren',
      'nav.profile': 'Profiel',
      'nav.orders': 'Bestellingen',
      'nav.favorites': 'Favorieten',
      'nav.cart': 'Winkelwagen',
      'nav.logout': 'Uitloggen',
      'nav.language': 'Taal',
      'nav.search': 'Zoeken',
      'nav.categories': 'Categorieën',
      'nav.deliverTo': 'Bezorgen naar',
      'nav.policies': 'Beleid',
      'nav.customerSupport': 'Klantenservice',
      'nav.updatePassword': 'Wachtwoord bijwerken',
      'nav.orderStatus': 'Bestelstatus',
      'nav.browsingHistory': 'Geschiedenis',
      'nav.noBrowsingHistory': 'Geen geschiedenis gevonden',
      'nav.vehicles': 'Koop voertuigen',
      'nav.property': 'Koop vastgoed',
      'nav.vehiclesAndPets': 'Voertuigen & huisdieren',
      'nav.softwareCenter': 'Softwarecentrum',
      'nav.softwareDigital': 'Koop software en digitale producten',
      'nav.becomeSeller': 'Verkoper worden',

      // User Types
      'userType.b2b': 'B2B',
      'userType.b2c': 'B2C',

      // Authentication
      'auth.welcome': 'Welkom!',
      'auth.loginToContinue': 'Log in op Merchants Bazaar om door te gaan.',
      'auth.login': 'Inloggen',
      'auth.loggingIn': 'Inloggen...',
      'auth.signup': 'Registreren',
      'auth.email': 'Uw e-mailadres',
      'auth.password': 'Uw wachtwoord',
      'auth.rememberMe': 'Onthoud mij',
      'auth.forgotPassword': 'Wachtwoord vergeten?',
      'auth.loginWithGoogle': 'Inloggen met Google',
      'auth.loginWithApple': 'Inloggen met Apple',
      'auth.loginWithTwitter': 'Inloggen met X',
      'auth.dontHaveAccount': 'Nog geen account?',

      // Common UI Elements
      'common.loading': 'Laden...',
      'common.error': 'Fout',
      'common.success': 'Succes',
      'common.save': 'Opslaan',
      'common.cancel': 'Annuleren',
      'common.edit': 'Bewerken',
      'common.delete': 'Verwijderen',
      'common.view': 'Bekijken',
      'common.close': 'Sluiten',
      'common.back': 'Terug',
      'common.next': 'Volgende',
      'common.previous': 'Vorige',
      'common.submit': 'Verzenden',
      'common.search': 'Zoeken',
      'common.filter': 'Filteren',
      'common.sort': 'Sorteren',
      'common.all': 'Alles',
      'common.select': 'Selecteren',
      'common.required': 'Verplicht',
      'common.optional': 'Optioneel',

      // Product UI
      'product.addToCart': 'In winkelwagen',
      'product.buyNow': 'Nu kopen',
      'product.price': 'Prijs',
      'product.originalPrice': 'Originele prijs',
      'product.discount': 'Korting',
      'product.inStock': 'Op voorraad',
      'product.outOfStock': 'Niet op voorraad',
      'product.quantity': 'Hoeveelheid',
      'product.description': 'Beschrijving',
      'product.specifications': 'Specificaties',
      'product.reviews': 'Beoordelingen',
      'product.rating': 'Beoordeling',
      'product.recommended': 'Aanbevolen producten',
      'product.related': 'Gerelateerde producten',
      'product.color': 'Kleur',
      'product.size': 'Maat',
      'product.selectColor': 'Kleur selecteren',
      'product.selectSize': 'Maat selecteren',

      // Home
      'home.recommendedProducts': 'Aanbevolen producten',
      'home.browserHistory': 'Geschiedenis',
      'home.products': 'Producten',
      'home.viewAll': 'Alles bekijken',
      'home.getProductsByCategory': 'Producten per categorie ophalen',
      'home.exploreTitle': 'Verken MerchantsBazaar',
      'home.exploreShop': 'Winkel',
      'home.exploreShopDesc': 'Alle producten van geverifieerde verkopers. Gratis verzending.',
      'home.exploreCategories': 'Categorieën',
      'home.exploreCategoriesDesc': 'Elektronica, mode, wonen, sport, gezondheid en meer.',
      'home.exploreAIShopping': 'AI Shopping',
      'home.exploreAIShoppingDesc': 'Slimme aanbevelingen en chat. Beste deals met AI.',
      'home.exploreVoiceShopping': 'Voice Shopping',
      'home.exploreVoiceShoppingDesc': 'Handenvrij shoppen. Zeg "In winkelwagen" of "Naar winkel".',
      'home.exploreAuctions': 'Veilingen',
      'home.exploreAuctionsDesc': 'Live veilingen. Bied op producten van geverifieerde verkopers.',
      'home.heroTitle': 'Producten, veilingen, advertenties & AI',
      'home.heroSubtitle':
        'Catalogus, live veilingen, voertuigen, vastgoed en huisdieren, met AI-assistenten—alles op één wereldmarktplaats.',
      'home.heroFeatures':
        'AI-aanbevelingen, spraak- en beeldzoeken, immersive passen—plus advertenties voor auto’s, wonen en huisdieren.',
      'home.heroEyebrow': 'Wereldwijde B2B- en B2C-marktplaats',
      'home.heroPillShop': 'Winkelcatalogus',
      'home.heroPillAuctions': 'Live veilingen',
      'home.heroPillClassifieds': 'Advertenties',
      'home.heroPillAI': 'AI-winkelen',
      'home.heroTrustVerifiedTitle': 'Geverifieerde verkopers',
      'home.heroTrustVerifiedDesc': 'Betrouwbare winkels & reviews',
      'home.heroTrustSecureTitle': 'Veilige betaling',
      'home.heroTrustSecureDesc': 'Met vertrouwen afrekenen',
      'home.heroTrustReachTitle': 'Wereldwijd bereik',
      'home.heroTrustReachDesc': 'B2B en B2C op één plek',

      // Shop and Filter UI
      'shop.clearAllFilters': 'Alle filters wissen',
      'shop.category': 'Categorie',
      'shop.subCategory': 'Subcategorie',
      'shop.brand': 'Merk',
      'shop.reviews': 'Beoordelingen',
      'shop.sortOptions': 'Sorteeropties',
      'shop.supplierRegions': 'Leveranciersregio\'s',
      'shop.newArrivals': 'Nieuw binnen',
      'shop.bestSellers': 'Bestsellers',
      'shop.discounts': 'Kortingen',
      'shop.sortBy': 'Sorteren op',
      'shop.price': 'Prijs',
      'shop.new': 'Nieuw binnen',
      'shop.ascending': 'Oplopend',
      'shop.descending': 'Aflopend',
      'shop.openFilters': 'Filters openen',
      'shop.searchResults': 'Zoekresultaten',
      'shop.andUp': 'en hoger',

      // Footer
      'footer.aboutText': 'We brengen u moderne en minimalistische producten met een onverslaanbare waarde. Bedankt voor uw aankoop bij ons.',
      'footer.securePayment': 'Veilige betaling',
      'footer.legalPolicies': 'Juridisch en beleid',
      'footer.termsConditions': 'Algemene voorwaarden',
      'footer.privacyPolicy': 'Privacybeleid',
      'footer.cookiesPolicy': 'Cookiebeleid',
      'footer.cookiesPreferences': 'Cookievoorkeuren',
      'footer.shippingPolicies': 'Verzendbeleid',
      'footer.quickLinks': 'Snelle links',
      'footer.blogNews': 'Blog en nieuws',
      'footer.home': 'Home',
      'footer.products': 'Producten',
      'footer.categories': 'Categorieën',
      'footer.aiShopping': 'AI Shopping',
      'footer.careers': 'Carrière',
      'footer.about': 'Over ons',
      'footer.contactUs': 'Contact',
      'footer.blog': 'Blog',
      'footer.news': 'Nieuws',
      'footer.dataManagement': 'Gegevensbeheer',
      'footer.dataManagementTagline': 'Voorraad, analyses en BI in één platform voor verkopers.',
      'footer.dataManagementUseApp': 'Data Management gebruiken',
      'footer.dataManagementLearnMore': 'MerchantsBazaar Data Management-platform',
      'footer.community': 'Merchants Bazaar Community',
      'footer.communityTagline': 'Ruimtes, discussies en updates voor verkopers en kopers.',
      'footer.communityVisit': 'Naar community',
      'footer.subscribe': 'Abonneren',
      'footer.getUpdates': 'Ontvang updates en exclusieve aanbiedingen.',
      'footer.emailPlaceholder': 'Uw e-mail',
      'footer.subscribeButton': 'Abonneren',
      'footer.copyright': 'Alle rechten voorbehouden.',
      'footer.pleaseEnterEmail': 'Voer uw e-mail in!',
      'footer.currency': 'Valuta',
      'footer.country': 'Land',
      'footer.stayConnected': 'Blijf verbonden',
      'footer.followUs': 'Volg ons',

      // Cart & Checkout
      'cart.title': 'Winkelwagen',
      'cart.empty': 'Uw winkelwagen is leeg',
      'cart.remove': 'Verwijderen',
      'cart.update': 'Bijwerken',
      'cart.subtotal': 'Subtotaal',
      'cart.shipping': 'Verzending',
      'cart.tax': 'Belasting',
      'cart.total': 'Totaal',
      'cart.checkout': 'Afrekenen',
      'cart.continueShopping': 'Verder winkelen',

      // Categories
      'category.Electronics': 'Elektronica',
      'category.Fashion and Apparel': 'Mode en kleding',
      'category.Beauty and Personal Care': 'Schoonheid en persoonlijke verzorging',
      'category.Health and Wellness': 'Gezondheid en welzijn',
      'category.Home & Kitchen': 'Huis & keuken',
      'category.Baby & Kids': 'Baby & kinderen',
      'category.Automotive & Tools': 'Auto & gereedschap',
      'category.Mobile & Accessories': 'Mobiel & accessoires',
      'category.Computers & Accessories': 'Computers & accessoires',
      'category.Grocery & Food': 'Boodschappen & eten',
      'category.Sports & Outdoors': 'Sport & outdoor',
      'category.Books & Stationery': 'Boeken & schrijfwaren',
      'category.Gaming & Entertainment': 'Gaming & entertainment',
      'category.Pet Supplies': 'Huisdierbenodigdheden',
      'category.Jewelry & Watches': 'Sieraden & horloges',
      'category.Industrial & Scientific': 'Industrieel & wetenschappelijk',
      'category.Bags, Wallets & Luggage': 'Tassen, portemonnees & bagage',
      'category.Art & Craft Supplies': 'Kunst- & knutselbenodigdheden',
    },
  };

  availableLanguages: Language[] = [
    { code: 'en', name: 'English', flag: '🇺🇸', direction: 'ltr' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', direction: 'ltr' },
    { code: 'es', name: 'Español', flag: '🇪🇸', direction: 'ltr' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', direction: 'rtl' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', direction: 'ltr' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱', direction: 'ltr' },
  ];

  constructor(private browserService: BrowserService) {
    // Initialize language from localStorage or browser language
    const savedLanguage = this.browserService.getLocalStorage('selectedLanguage');
    const browserLanguage = this.browserService.getBrowserLanguage();
    const initialLanguage = savedLanguage || this.getSupportedLanguage(browserLanguage) || 'en';

    this.setLanguage(initialLanguage);
  }

  /**
   * Get supported language from browser language
   */
  private getSupportedLanguage(browserLang: string): string | null {
    const supportedLanguages = this.availableLanguages.map((lang) => lang.code);
    const langCode = browserLang.split('-')[0];
    return supportedLanguages.includes(langCode) ? langCode : null;
  }

  /**
   * Set current language
   */
  setLanguage(languageCode: string): void {
    if (this.availableLanguages.some((lang) => lang.code === languageCode)) {
      this.currentLanguageSubject.next(languageCode);
      this.browserService.setLocalStorage('selectedLanguage', languageCode);

      // Update document direction for RTL languages
      this.browserService.executeInBrowser(() => {
        const doc = this.browserService.getDocument();
        if (doc) {
          const htmlElement = doc.documentElement;
          const language = this.getCurrentLanguageObject();
          htmlElement.setAttribute('dir', language.direction);
          htmlElement.setAttribute('lang', languageCode);
        }
      });
    }
  }

  /**
   * Get current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get current language object
   */
  getCurrentLanguageObject(): Language {
    return (
      this.availableLanguages.find((lang) => lang.code === this.getCurrentLanguage()) ||
      this.availableLanguages[0]
    );
  }

  /**
   * Translate a key
   */
  translate(key: string): string {
    const currentLang = this.getCurrentLanguage();
    const translations = this.translations[currentLang];

    if (translations && translations[key]) {
      return translations[key];
    }

    // Fallback to English if translation not found
    const englishTranslations = this.translations['en'];
    if (englishTranslations && englishTranslations[key]) {
      return englishTranslations[key];
    }

    // Return key if no translation found
    return key;
  }

  /**
   * Get available languages
   */
  getAvailableLanguages(): Language[] {
    return this.availableLanguages;
  }

  /**
   * Check if current language is RTL
   */
  isRTL(): boolean {
    return this.getCurrentLanguageObject().direction === 'rtl';
  }
}
